package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import at.theatervogelweide.markdown.renderMarkdown
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.ElementBuilder
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLTextAreaElement
import org.w3c.dom.events.Event
import org.w3c.dom.EventInit
import org.jetbrains.compose.web.events.SyntheticKeyboardEvent

private val field = Bem("admin-field")
private val textarea = Bem("admin-textarea")
private val markdown = Bem("admin-markdown")
private val toolbar = Bem("admin-toolbar")
private val button = Bem("admin-button")
private val dialog = Bem("admin-dialog")
private val stack = Bem("admin-stack")

private val DialogElement = ElementBuilder { document.createElement("dialog") as HTMLElement }

private enum class MarkdownView { WRITE, PREVIEW }

/** A button of the formatting toolbar (design system v56): its name, shortcut key and stroke icon. */
private enum class FormatButton(val label: String, val key: Char?, val icon: String, val groupEnd: Boolean = false) {
    BOLD("Fett", 'B', """<path d="M6 4h4.5a3 3 0 0 1 0 6H6zM6 10h5.5a3 3 0 0 1 0 6H6z"/>"""),
    ITALIC("Kursiv", 'I', """<path d="M9 4h6M5 16h6M12 4 8 16"/>"""),
    LINK(
        "Link",
        'K',
        """<path d="M8.6 11.4a3 3 0 0 0 4.2 0l2.5-2.5a3 3 0 0 0-4.2-4.2l-1 1M11.4 8.6a3 3 0 0 0-4.2 0l-2.5 2.5a3 3 0 0 0 4.2 4.2l1-1"/>""",
        groupEnd = true,
    ),
    BULLET_LIST("Liste", null, """<path d="M8 5h9M8 10h9M8 15h9"/><path d="M3.5 5h.01M3.5 10h.01M3.5 15h.01" stroke-width="2.4"/>"""),
    NUMBERED_LIST(
        "Nummerierte Liste",
        null,
        """<path d="M8 5h9M8 10h9M8 15h9M2.8 3.8 4 3v4M2.7 9.2a1.1 1.1 0 0 1 2.1.5L2.7 12h2.2M2.7 13.5h2l-1.1 1.3a1.1 1.1 0 1 1-1 1.7"/>""",
        groupEnd = true,
    ),
    HEADING("Überschrift", null, """<path d="M5 4v12M15 4v12M5 10h10"/>"""),
}

/** Whether shortcuts take ⌘ rather than Ctrl, as on macOS, iPadOS and iOS. */
private val usesCommandKey: Boolean by lazy {
    Regex("Mac|iPhone|iPad|iPod").containsMatchIn(window.navigator.platform + " " + window.navigator.userAgent)
}

/**
 * A Markdown text area with a "Schreiben"/"Vorschau" switch and a formatting toolbar (#166); the preview renders like
 * the public site (see [renderMarkdown]). Switching never loses input. The toolbar and its shortcuts (Ctrl/⌘+B, I, K)
 * only write Markdown into the text, each change as one step of the text area's undo.
 */
@Composable
fun AdminMarkdownField(
    id: String,
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    error: String? = null,
    hint: String? = "Markdown wird unterstützt: **fett**, *kursiv*, Listen mit - und Links als [Text](https://…).",
    rows: Int = 8,
    optional: Boolean = false,
) {
    var view by remember { mutableStateOf(MarkdownView.WRITE) }
    var textArea by remember { mutableStateOf<HTMLTextAreaElement?>(null) }
    // the toolbar's one tab stop
    var current by remember { mutableStateOf(FormatButton.BOLD) }
    var link by remember { mutableStateOf<LinkTarget?>(null) }
    // where the selection was when the link dialog opened, for cancelling it
    var linkSelection by remember { mutableStateOf(0 to 0) }
    val buttons = remember { mutableMapOf<FormatButton, HTMLButtonElement>() }
    val messageId = "$id-message"
    val previewId = "$id-preview"

    fun format(action: FormatButton) {
        val area = textArea ?: return
        val text = area.value
        val start = area.selectionStart ?: 0
        val end = area.selectionEnd ?: start
        when (action) {
            FormatButton.BOLD -> area.applyEdit(toggleInline(text, start, end, InlineFormat.BOLD))
            FormatButton.ITALIC -> area.applyEdit(toggleInline(text, start, end, InlineFormat.ITALIC))
            FormatButton.LINK -> {
                // none in code
                link = linkAt(text, start, end)?.also { linkSelection = start to end }
            }
            FormatButton.BULLET_LIST -> area.applyEdit(toggleLines(text, start, end, LineFormat.BULLET_LIST))
            FormatButton.NUMBERED_LIST -> area.applyEdit(toggleLines(text, start, end, LineFormat.NUMBERED_LIST))
            FormatButton.HEADING -> area.applyEdit(toggleLines(text, start, end, LineFormat.HEADING))
        }
    }

    Div({ classes(field.block) }) {
        Div({ classes(field.element("label-row").className) }) {
            Label(forId = id, attrs = { classes(field.element("label").className) }) {
                Text(label)
                if (optional) {
                    Text(" ")
                    Span({ classes(field.element("optional").className) }) { Text("(optional)") }
                }
            }
            AdminSegmentedControl(
                options = listOf(MarkdownView.WRITE to "Schreiben", MarkdownView.PREVIEW to "Vorschau"),
                selected = view,
                onSelect = { view = it },
                label = "Ansicht von $label",
            )
        }

        Div({
            classes(toolbar.block, toolbar.modifier("field"))
            attr("role", "toolbar")
            attr("aria-label", "Formatierung von $label")
            attr("aria-controls", id)
            if (view == MarkdownView.PREVIEW) {
                attr("hidden", "")
            }
            onKeyDown { event ->
                val all = FormatButton.entries
                val next = when (event.key) {
                    "ArrowRight" -> all[(current.ordinal + 1) % all.size]
                    "ArrowLeft" -> all[(current.ordinal - 1 + all.size) % all.size]
                    "Home" -> all.first()
                    "End" -> all.last()
                    else -> return@onKeyDown
                }
                event.preventDefault()
                current = next
                buttons[next]?.focus()
            }
        }) {
            FormatButton.entries.forEach { action ->
                val shortcut = action.key?.let { if (usesCommandKey) "⌘$it" else "Strg+$it" }
                Button({
                    attr("type", "button")
                    classes(button.block, button.modifier("icon"))
                    attr("tabindex", if (action == current) "0" else "-1")
                    attr("aria-label", action.label)
                    attr("title", shortcut?.let { "${action.label} ($it)" } ?: action.label)
                    action.key?.let { attr("aria-keyshortcuts", (if (usesCommandKey) "Meta+" else "Control+") + it) }
                    ref {
                        buttons[action] = it
                        onDispose { buttons.remove(action) }
                    }
                    onClick {
                        current = action
                        format(action)
                    }
                }) {
                    DisposableEffect(Unit) {
                        scopeElement.innerHTML = """<svg aria-hidden="true" viewBox="0 0 20 20" fill="none" stroke="currentColor" """ +
                            """stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${action.icon}</svg>"""
                        onDispose { }
                    }
                }
                if (action.groupEnd) {
                    Span({
                        classes(toolbar.element("separator").className)
                        attr("aria-hidden", "true")
                    })
                }
            }
        }

        TextArea(value) {
            classes(textarea.block, textarea.modifier("markdown"))
            id(id)
            attr("rows", rows.toString())
            onInput { onValueChange(it.value) }
            onKeyDown { event ->
                formatShortcut(event)?.let { action ->
                    event.preventDefault()
                    format(action)
                }
            }
            ref {
                textArea = it
                onDispose { textArea = null }
            }
            if (view == MarkdownView.PREVIEW) {
                attr("hidden", "")
            }
            if (error != null) {
                attr("aria-invalid", "true")
            }
            if (error != null || hint != null) {
                attr("aria-describedby", messageId)
            }
        }

        if (view == MarkdownView.PREVIEW) {
            val html = renderMarkdown(value)
            Div({
                classes(markdown.block)
                id(previewId)
                attr("aria-label", "Vorschau von $label")
                attr("role", "region")
            }) {
                DisposableEffect(html) {
                    scopeElement.innerHTML = html.ifBlank { "<p>Noch kein Text.</p>" }
                    onDispose { }
                }
            }
        }

        when {
            error != null -> Span({
                classes(field.element("error").className)
                id(messageId)
            }) { Text(error) }

            hint != null -> Span({
                classes(field.element("hint").className)
                id(messageId)
            }) { Text(hint) }
        }

        MarkdownLinkDialog(
            id = "$id-link",
            target = link,
            onApply = { edit ->
                link = null
                textArea?.run {
                    if (edit != null) {
                        applyEdit(edit)
                    } else {
                        focus()
                        setSelectionRange(linkSelection.first, linkSelection.second)
                    }
                }
            },
            onCancel = {
                link = null
                textArea?.run {
                    focus()
                    setSelectionRange(linkSelection.first, linkSelection.second)
                }
            },
        )
    }
}

/** The toolbar action of a shortcut: Ctrl+B, I, K, or ⌘ on Apple devices. */
private fun formatShortcut(event: SyntheticKeyboardEvent): FormatButton? {
    val command = if (usesCommandKey) event.metaKey && !event.ctrlKey else event.ctrlKey && !event.metaKey
    if (!command || event.altKey || event.shiftKey || event.key.length != 1) {
        return null
    }
    // uppercase() may give more than one character (ß is SS)
    val key = event.key.uppercase()
    return FormatButton.entries.firstOrNull { it.key?.toString() == key }
}

/**
 * Applies [edit] the way typing would, so the text area's own undo takes it back in one step and `input` reaches
 * [AdminMarkdownField]'s `onValueChange`; then selects the changed text.
 */
private fun HTMLTextAreaElement.applyEdit(edit: MarkdownEdit) {
    focus()
    if (edit.start == edit.end && edit.replacement.isEmpty()) {
        setSelectionRange(edit.selectionStart, edit.selectionEnd)
        return
    }
    setSelectionRange(edit.start, edit.end)
    val expected = edit.applyTo(value)
    val command = if (edit.replacement.isEmpty()) "delete" else "insertText"
    // browsers may change the text without an input event, which onValueChange needs
    var announced = false
    val listener: (Event) -> Unit = { announced = true }
    addEventListener("input", listener)
    val done = document.asDynamic().execCommand(command, false, edit.replacement) == true
    removeEventListener("input", listener)
    if (!done || value != expected) {
        // without execCommand (it is deprecated, though every browser has it): no undo step, but the same text
        value = expected
        announced = false
    }
    if (!announced) {
        dispatchEvent(Event("input", EventInit(bubbles = true)))
    }
    setSelectionRange(edit.selectionStart, edit.selectionEnd)
}

/**
 * "Link einfügen" or, on an existing link, "Link bearbeiten" (design system v56): its text and address, checked on
 * submit. [onApply] gets the edit for the text area, or `null` when nothing changes; it is called once the dialog has
 * closed, so the focus can go back to the text area.
 */
@Composable
private fun MarkdownLinkDialog(id: String, target: LinkTarget?, onApply: (MarkdownEdit?) -> Unit, onCancel: () -> Unit) {
    var element by remember { mutableStateOf<HTMLElement?>(null) }
    val open = target != null

    DisposableEffect(open, element) {
        val dialogElement = element?.asDynamic()
        if (dialogElement != null) {
            if (open && dialogElement.open != true) {
                dialogElement.showModal()
                val first = if (target?.text.isNullOrBlank()) "$id-text" else "$id-adresse"
                (document.getElementById(first) as? HTMLElement)?.focus()
            } else if (!open && dialogElement.open == true) {
                dialogElement.close()
            }
        }
        onDispose { }
    }

    // closes the dialog before the callback, so closing doesn't move the focus after the callback placed it
    fun closeThen(action: () -> Unit) {
        element?.asDynamic()?.close()
        action()
    }

    TagElement(DialogElement, {
        classes(dialog.block)
        id(id)
        attr("aria-labelledby", "$id-title")
        ref {
            element = it
            onDispose { element = null }
        }
        // Escape
        addEventListener("cancel") { event ->
            event.preventDefault()
            closeThen(onCancel)
        }
    }) {
        if (target == null) {
            return@TagElement
        }
        // a fresh form for every link
        key(target) {
            LinkForm(id, target, onSubmit = { edit -> closeThen { onApply(edit) } }, onCancel = { closeThen(onCancel) })
        }
    }
}

@Composable
private fun LinkForm(id: String, target: LinkTarget, onSubmit: (MarkdownEdit?) -> Unit, onCancel: () -> Unit) {
    // the text as the one-line field shows it, which drops its line breaks
    val shown = target.text.replace("\n", "").replace("\r", "")
    var text by remember { mutableStateOf(shown) }
    var address by remember { mutableStateOf(target.address) }
    var textError by remember { mutableStateOf<String?>(null) }
    var addressError by remember { mutableStateOf<String?>(null) }

    // without trimming, as taking away its spaces is an edit too
    fun unchanged(value: String) = value == shown

    fun submit() {
        // an existing link's address stands, even one the dialog wouldn't take (a bare `www.` address's `http://`)
        val errors = linkErrors(text.trim(), address.trim()).let { (text, error) ->
            text to error.takeUnless { target.existing && address.trim() == target.address }
        }
        textError = errors.first
        addressError = errors.second
        when {
            errors.first != null || errors.second != null -> Unit
            // unchanged: no edit, so no empty step in the text area's undo
            target.existing && unchanged(text) && address.trim() == target.address -> onSubmit(null)
            // the spaces and line breaks of an existing link's text, which the one-line field drops, stay unless the
            // text changed
            else -> onSubmit(insertLink(target, if (unchanged(text)) target.text else text.trim(), address.trim()))
        }
    }

    H2({
        classes(dialog.element("title").className)
        id("$id-title")
    }) { Text(if (target.existing) "Link bearbeiten" else "Link einfügen") }

    Div({
        classes(stack.block)
        // Enter submits; it must not reach a form around the field, which would submit the editor
        onKeyDown { event ->
            // not while an input method composes: its Enter takes the composed text
            if (event.key == "Enter" && event.nativeEvent.asDynamic().isComposing != true && (event.target as? HTMLElement)?.tagName == "INPUT") {
                event.preventDefault()
                submit()
            }
        }
    }) {
        AdminTextField(
            id = "$id-text",
            label = "Text",
            value = text,
            onValueChange = {
                text = it
                textError = null
            },
            error = textError,
            required = false,
            autocomplete = "off",
        )
        AdminTextField(
            id = "$id-adresse",
            label = "Adresse",
            value = address,
            onValueChange = {
                address = it
                addressError = null
            },
            error = addressError,
            hint = "Eine Webadresse mit https:// oder eine E-Mail-Adresse mit mailto:",
            required = false,
            autocomplete = "off",
            inputMode = "url",
        )
        Div({ classes(dialog.element("actions").className) }) {
            if (target.existing) {
                AdminButton(onClick = { onSubmit(removeLink(target)) }) { Text("Link entfernen") }
            }
            AdminButton(onClick = onCancel) { Text("Abbrechen") }
            AdminButton(variant = ButtonVariant.PRIMARY, onClick = ::submit) {
                Text(if (target.existing) "Übernehmen" else "Einfügen")
            }
        }
    }
}

/**
 * Markdown for a person who may not change it (design system v26): the label and the rendered text, without the
 * switch, the toolbar, the text area or the Markdown hint.
 */
@Composable
fun AdminMarkdownView(id: String, label: String, value: String) {
    val labelId = "$id-label"
    val html = renderMarkdown(value)

    Div({ classes(field.block) }) {
        Span({
            classes(field.element("label").className)
            id(labelId)
        }) { Text(label) }
        Div({
            classes(markdown.block)
            id(id)
            attr("aria-labelledby", labelId)
            attr("role", "region")
        }) {
            DisposableEffect(html) {
                scopeElement.innerHTML = html.ifBlank { "<p>Noch kein Text.</p>" }
                onDispose { }
            }
        }
    }
}
